import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../services/api';

interface AdminActionFeedState {
  unreadCount: number;
  capped: boolean;
  // Set of offeringIds that appear in the unread slice of the feed.
  // Used to drive per-service red-dot indicators on the My Services page.
  unreadOfferingIds: Set<string>;
}

const EMPTY_STATE: AdminActionFeedState = { unreadCount: 0, capped: false, unreadOfferingIds: new Set() };

export function useAdminActionFeed(token: string | null) {
  const [state, setState] = useState<AdminActionFeedState>(EMPTY_STATE);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const pollUnread = useCallback(async () => {
    if (!token) return;
    try {
      const counts = await api.getAdminActionUnreadCount(token);
      if (counts.count === 0) {
        setState({ unreadCount: 0, capped: counts.capped, unreadOfferingIds: new Set() });
        return;
      }
      // There are unread actions — fetch the top of the feed so we can flag affected services.
      // We fetch up to 50 (covers virtually every realistic case; the badge caps at 100 anyway).
      const page = await api.getAdminActionFeed(token, null, 50);
      const rows = page.content ?? [];
      // The feed is sorted by id DESC. The first `unreadCount` rows are the unread slice.
      const unreadSlice = rows.slice(0, Math.min(counts.count, rows.length));
      const offeringIds = new Set<string>();
      for (const r of unreadSlice) {
        if (r.offeringId) offeringIds.add(r.offeringId);
      }
      setState({ unreadCount: counts.count, capped: counts.capped, unreadOfferingIds: offeringIds });
    } catch {
      // silently ignore poll errors
    }
  }, [token]);

  const clearLocal = useCallback(() => {
    setState({ unreadCount: 0, capped: false, unreadOfferingIds: new Set() });
  }, []);

  const clearOffering = useCallback((offeringId: string) => {
    setState((prev) => {
      if (!prev.unreadOfferingIds.has(offeringId)) return prev;
      const next = new Set(prev.unreadOfferingIds);
      next.delete(offeringId);
      return { ...prev, unreadOfferingIds: next };
    });
  }, []);

  useEffect(() => {
    if (!token) return;
    pollUnread();
    intervalRef.current = setInterval(pollUnread, 60_000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [token, pollUnread]);

  return {
    unreadCount: state.unreadCount,
    capped: state.capped,
    unreadOfferingIds: state.unreadOfferingIds,
    pollUnread,
    clearLocal,
    clearOffering,
  };
}
